import {
  ceil,
  floor,
  isFinite,
  isNaN,
  isNumber,
  max,
  min,
  parseInt,
  round,
} from "lodash";
import { localize, shorten } from "@backpacker/number";
import { Locale } from "@linkfluence/models/locale";

export function isValid(number: any): boolean {
  return (
    !(isNaN(number) || !isFinite(number) || number === null) && isNumber(number)
  );
}

export function parseNumber(value: string): number | undefined {
  const parsedValue = parseInt(value);
  return isValid(parsedValue) ? parsedValue : undefined;
}

export function cap(number: number, cap: number): string {
  if (number <= cap) {
    return "" + number;
  } else {
    return "+" + cap;
  }
}

export function division(
  dividend: number,
  divisor: number,
  fixed?: number
): number | string | null {
  if (isValid(divisor) && isValid(dividend)) {
    const result = divisor !== 0 ? dividend / divisor : 0;
    return fixed ? result.toFixed(fixed) : result;
  } else {
    return null;
  }
}

export function percent(value: number, total: number): number {
  return value && total ? (value / total) * 100 : 0;
}

export function log10(value: number): number {
  return Math.log(value) / Math.LN10;
}

export function between(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

export function evolution(before: number, after: number): number;
export function evolution(
  before: number,
  after: number,
  fixed?: number
): string | number | null {
  if (isValid(before) && isValid(after)) {
    const result =
      before > 0 ? ((after - before) / before) * 100 : after > 0 ? 100 : 0;
    return fixed ? result.toFixed(fixed) : round(result, 1);
  } else {
    return null;
  }
}

/**
 * e.g. evolutionPercentagePoint(40%, 44%) === 4pp (4 percentage point increase)
 * @param before in percentage
 * @param after in percentage
 * @returns arithmetic difference between before and after
 */
export function evolutionPercentagePoint(before: number, after: number) {
  if (isValid(before) && isValid(after)) {
    return after - before;
  } else {
    return null;
  }
}

function lgOrExp(value: number, type?: "lg" | "exp") {
  if (type === "lg") {
    return value > 0 ? Math.log(value) : value;
  } else if (type === "exp") {
    return Math.exp(value / 1000000);
  } else {
    return value;
  }
}

export function normalize(
  array: number[],
  minimum: number,
  maximum: number,
  value: number,
  type?: "lg" | "exp"
): number {
  if (Array.isArray(array)) {
    const _maximum = lgOrExp(maximum, type);
    const _minimum = lgOrExp(minimum, type);
    const _value = lgOrExp(value, type);
    const floor = array[0];
    if (array.length > 1) {
      const ceil = array[1];
      const diff = _maximum - _minimum;
      return diff > 0
        ? ((ceil - floor) * (_value - _minimum)) / diff + floor
        : ceil;
    } else {
      return floor;
    }
  } else {
    return array;
  }
}

export const roundNDecimal = (value: number, nbDecimal: number): number => {
  const coef = Math.pow(10, nbDecimal);
  return Math.round(value * coef) / coef;
};

/*
  Returns a range of size `width` that tries to keep `i` in the middle
  The result is kept inside [1, end]
  Usage: for pagination, navigation to neighbour pages
  e.g.
    (i = 2, end = 10) => [1, 5]
    (i = 5, end = 10) => [3, 7]
    (i = 9, end = 10) => [6, 10]
    (i = 3, end = 10, width = 3) => [2, 4]
 */
export function getBoundingRange(
  i: number,
  end: number,
  width: number = 5
): number[] {
  const paddingLeft = floor(width / 2);
  const paddingRight = ceil(width / 2) - 1;

  const lastWindowStart = end - paddingRight;

  if (i <= paddingLeft) {
    // First window
    return [1, min([width, end]) || 1];
  } else if (i > lastWindowStart) {
    // Last window
    return [max([1, end - width + 1]) || 1, end];
  } else {
    // Middle window
    return [max([1, i - paddingLeft]) || 1, min([i + paddingRight, end]) || 1];
  }
}

/**
 * Returns the positive modulo of a number
 * @param value
 * @param modulo
 */
export const mod = (value: number, modulo: number): number => {
  const m = ((value % modulo) + modulo) % modulo;
  return m < 0 ? m + Math.abs(modulo) : m;
};

export const isFloat = (number: number) => number % 1 !== 0;

export const getResponsiveCount = (
  count: number,
  locale: Locale,
  isPercentage: boolean,
  isNarrow: boolean
) => {
  const total = isPercentage ? parseFloat("" + count) : parseInt("" + count);
  if (isPercentage) {
    return shorten(locale, total, 3, true, 1) + "%";
  }
  if (isNarrow) {
    return shorten(locale, total, 3, false, 1);
  }
  return localize(locale, total);
};
